import React, { FC, useEffect, useState } from 'react';
import {
  Box,
  Button,
  ButtonAppearance,
  ButtonSize,
  Icon,
  IconSize,
  Text,
  Spinner,
  ToastAction,
  useToastsDispatch,
  ToastType,
} from '@bechtel/gds';
import { Trans, t } from '@lingui/macro';
import { DetailSubContainerStyled } from '~/detail/styled';
import { useMutation, ApolloError } from '@apollo/client';
import {
  PinCostCodesInput,
  PinCostCodesPayload,
  UnpinCostCodesInput,
  UnpinCostCodesPayload,
  Scalars,
  PinCostCodesError,
  UnpinCostCodesError,
} from '@bechtel/types';
import { PIN_COST_CODES, UNPIN_COST_CODES } from '@bechtel/client-gql';
import { filter, find } from 'lodash';
import { CostCodeIndicatorStyled } from '../header/styled';
import { TSCostCode } from '~/detail/types';
import { FastField, useFormikContext } from 'formik';
import { get } from 'lodash';
import { PinUnpinError } from './error';

interface CostCodeHeaderProps {
  timesheetCostCode: TSCostCode;
  crewId: Scalars['ID'];
  formikFieldName: string;
  collapseAll: boolean;
  collapseList: string[];
  setCollapseList: (list: string[]) => void;
}

export const PinCostCodeToast = () => {
  return (
    <Box p={{ sm: '500' }} flexDirection={{ sm: 'column' }}>
      <Text family="graphik" weight="600" size="500">
        <Trans>Cost Code Pinned successfully.</Trans>
      </Text>
    </Box>
  );
};

export const UnPinCostCodeToast = () => {
  return (
    <Box p={{ sm: '500' }} flexDirection={{ sm: 'column' }}>
      <Text family="graphik" weight="600" size="500">
        <Trans>Cost Code Unpinned successfully.</Trans>
      </Text>
    </Box>
  );
};

export const CostCodeHeader: FC<CostCodeHeaderProps> = ({
  timesheetCostCode,
  crewId,
  formikFieldName,
  collapseAll,
  collapseList,
  setCollapseList,
}) => {
  const dispatchToast = useToastsDispatch();
  const pinnedCostCodeIdFieldName = `${formikFieldName}.pinnedCostCodeId`;
  const { costCode } = timesheetCostCode;
  const { values, setFieldValue } = useFormikContext();
  const pinnedCostCodeIdValue = get(values, pinnedCostCodeIdFieldName, '');
  const [pinUnPinError, setPinUnPinError] = useState<PinCostCodesError | UnpinCostCodesError | ApolloError | null>(
    null
  );

  const [
    pinCostCodes,
    { data: pinCostCodesData, loading: pinCostCodesLoading, error: pinCostCodesError },
  ] = useMutation<{ pinCostCodes: PinCostCodesPayload }, { input: PinCostCodesInput }>(PIN_COST_CODES, {
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      if (data.pinCostCodes.error) return;
      setFieldValue(pinnedCostCodeIdFieldName, data.pinCostCodes.costCodes?.timesheetCostCodes[0].id);
    },
  });

  const [
    unPinCostCodes,
    { data: unPinCostCodesData, loading: unPinCostCodesLoading, error: unPinCostCodesError },
  ] = useMutation<{ unpinCostCodes: UnpinCostCodesPayload }, { input: UnpinCostCodesInput }>(UNPIN_COST_CODES, {
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      if (data.unpinCostCodes.error) return;
      setFieldValue(pinnedCostCodeIdFieldName, '');
    },
  });

  const collapsedId = find(collapseList, (item) => item === costCode?.id);

  const handleClick = () => {
    if (collapsedId) {
      setCollapseList(filter(collapseList, (item) => item !== collapsedId));
    } else {
      setCollapseList([...collapseList, costCode.id]);
    }
  };

  const mutationError =
    get(pinCostCodesData, 'pinCostCodes.error', null) ||
    pinCostCodesError ||
    get(unPinCostCodesData, 'unpinCostCodes.error', null) ||
    unPinCostCodesError;

  useEffect(() => {
    if (!mutationError) return;
    setPinUnPinError(mutationError);
  }, [mutationError]);

  useEffect(() => {
    if (
      !pinCostCodesData ||
      !pinCostCodesData.pinCostCodes ||
      !pinCostCodesData.pinCostCodes.costCodes ||
      !pinCostCodesData.pinCostCodes.costCodes?.timesheetCostCodes ||
      pinCostCodesData.pinCostCodes.costCodes?.timesheetCostCodes.length == 0
    )
      return;
    const toastAction: ToastAction = {
      type: 'ADD_TOAST',
      payload: {
        toastType: ToastType.INFO,
        content: PinCostCodeToast,
        autoDismissTimeout: 1000,
      },
    };
    dispatchToast(toastAction);
  }, [dispatchToast, pinCostCodesData]);

  useEffect(() => {
    if (
      !unPinCostCodesData ||
      !unPinCostCodesData.unpinCostCodes ||
      !unPinCostCodesData.unpinCostCodes.timesheetCostCodeIds ||
      unPinCostCodesData.unpinCostCodes.timesheetCostCodeIds.length == 0
    )
      return;
    const toastAction: ToastAction = {
      type: 'ADD_TOAST',
      payload: {
        toastType: ToastType.INFO,
        content: UnPinCostCodeToast,
        autoDismissTimeout: 1000,
      },
    };
    dispatchToast(toastAction);
  }, [dispatchToast, unPinCostCodesData]);

  const handleClose = () => setPinUnPinError(null);

  return (
    <>
      <DetailSubContainerStyled style={{ marginBottom: 0 }}>
        <CostCodeIndicatorStyled />
        <FastField name={pinnedCostCodeIdFieldName} type="hidden"></FastField>
        <Box
          flexDirection={{ sm: 'row' }}
          flexGrow={{ sm: 1 }}
          alignItems={{ sm: 'center' }}
          justifyContent={{ sm: 'space-between' }}
          py={{ sm: '300' }}
          pl={{ sm: '500' }}
          pr={{ sm: '400' }}
        >
          <Box>
            <Text color="grey.1000" size="400" weight="600" family="graphik">
              {costCode.description}
            </Text>
          </Box>
          <Box alignItems={{ sm: 'center' }}>
            <Box mr={{ sm: '300' }}>
              {pinnedCostCodeIdValue ? (
                <Button
                  appearance={ButtonAppearance.SECONDARY}
                  size={ButtonSize.SMALL}
                  onClick={() => {
                    unPinCostCodes({
                      variables: {
                        input: {
                          timesheetCostCodeIds: [timesheetCostCode.pinnedCostCodeId],
                        },
                      },
                    });
                  }}
                  disabled={unPinCostCodesLoading}
                  data-testid="unpin-action"
                >
                  <Box ml={{ sm: '100' }} mr={{ sm: '300' }}>
                    {unPinCostCodesLoading && <Spinner />}
                    {!unPinCostCodesLoading && (
                      <Box style={{ position: 'relative' }}>
                        <Icon icon="thumbtack" size={IconSize.MEDIUM} fillColor="blue.800" />
                        <Icon
                          icon="slash"
                          size={IconSize.MEDIUM}
                          fillColor="blue.800"
                          style={{ position: 'absolute' }}
                        />
                      </Box>
                    )}
                  </Box>
                  <Box mr={{ sm: '300' }}>
                    <Text size={'300'} color={'grey.1000'}>
                      <Trans>Unpin</Trans>
                    </Text>
                  </Box>
                </Button>
              ) : (
                <Button
                  appearance={ButtonAppearance.SECONDARY}
                  size={ButtonSize.SMALL}
                  onClick={() => {
                    pinCostCodes({
                      variables: {
                        input: {
                          crewId: crewId,
                          costCodesIds: [costCode.id],
                        },
                      },
                    });
                  }}
                  disabled={pinCostCodesLoading}
                  data-testid="pin-action"
                >
                  <Box ml={{ sm: '100' }} mr={{ sm: '300' }}>
                    {pinCostCodesLoading && <Spinner />}
                    {!pinCostCodesLoading && <Icon icon="thumbtack" size={IconSize.MEDIUM} fillColor="blue.800" />}
                  </Box>
                  <Box mr={{ sm: '300' }}>
                    <Text size={'300'} color={'grey.1000'}>
                      <Trans>Pin</Trans>
                    </Text>
                  </Box>
                </Button>
              )}
            </Box>
            <Box onClick={handleClick} css={{ cursor: 'pointer' }}>
              <Button appearance={ButtonAppearance.SECONDARY} size={ButtonSize.SMALL}>
                <Box ml={{ sm: '100' }} mr={{ sm: '300' }}>
                  <Icon icon="compress-alt" size={IconSize.MEDIUM} fillColor="blue.800" />
                </Box>
                <Box mr={{ sm: '300' }}>
                  <Text size={'300'} color={'grey.1000'}>
                    {collapseAll || collapsedId ? t`Expand` : t`Collapse`}
                  </Text>
                </Box>
              </Button>
            </Box>
          </Box>
        </Box>
      </DetailSubContainerStyled>
      {pinUnPinError && <PinUnpinError error={pinUnPinError} close={handleClose}></PinUnpinError>}
    </>
  );
};
